import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { createChunk } from "@packages/lib/xlsx/chunk";
import { readRawData } from "@packages/lib/xlsx/read_raw_data";
import * as XLJS from "exceljs";
import { isEqual, omit } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSaveFileImportConfig } from "../../../../../apps/client/src/state/api/mutations/file_import_config";
import { FileDropzone } from "../../files";
import { ConfirmationStep, InstructionStep, Preview, Result } from "./steps";
import { ColumnMapping } from "./steps/column-mapping";
import "./style.css";
import {
  NewImportAndValidateProps,
  OrderGuideImportResponse,
  CustomerImportResponse,
  ConfirmV2ResultType,
  ErrorTextType,
  StepProps,
  StepLabelProps,
} from "./types";

export const NewImportAndValidateModal = ({
  title,
  templateUrl,
  batchSize,
  column,
  parser,
  toggleModal,
  onConfirm,
  refetchApi,
  fieldMapFromConfig,
  defaultFieldMap,
  destinationField,
  destinationFieldRequired,
  featureType,
  videoUrl,
  isUseV2,
  previousSuccessfulImport,
  uploadFile,
}: NewImportAndValidateProps) => {
  let LIMIT = 4;
  if (isUseV2) {
    LIMIT += 1;
  }
  const BATCH_SIZE = batchSize || 1000;

  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(false);
  const [processed, setProcessed] = useState<number | null>(null);
  const [errorText, setErrorText] = useState<ErrorTextType | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<any>([]);
  const [rawRow, setRawRow] = useState<any[]>([]);
  const [rawHeader, setRawHeader] = useState<any>([]);
  const [validatedRow, setValidatedRow] = useState<any[]>([]);
  const [filteredRow, setFilteredRow] = useState<any[]>([]);
  const [confirmV2Result, setConfirmV2Result] = useState<ConfirmV2ResultType>(
    {},
  );
  const [enableCache, setEnableCache] = useState<boolean>(true);

  const initColumnMapping = fieldMapFromConfig
    ? new Map<string, string>(Object.entries(fieldMapFromConfig))
    : new Map<string, string>(Object.entries(defaultFieldMap));
  const [columnMap, setColumnMap] =
    useState<Map<string, string | null>>(initColumnMapping);
  const [_, saveFileImportConfig] = useSaveFileImportConfig();

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
      return;
    }

    if (activeStep + 1 <= LIMIT) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped(newSkipped);
    } else {
      toast.error("Please complete the previous step");
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      toast.error("Please complete the previous step");
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
    emptyErrorHandler();
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleFilesDrop = useCallback((newFiles: File[]): void => {
    setFiles((prevFiles) => {
      return [...prevFiles, ...newFiles];
    });
  }, []);

  const handleFileRemove = useCallback((file: File): void => {
    setFiles((prevFiles) => {
      // @ts-ignore
      return prevFiles.filter((_file) => _file.path !== file.path);
    });
  }, []);

  const emptyErrorHandler = useCallback(() => {
    setError(false);
    setErrorText(null);
  }, []);

  const handleFilesRemoveAll = useCallback((): void => {
    setFiles([]);
  }, []);

  const handleSaveColumnMapping = () => {
    const mapping: any = {};
    columnMap.forEach((value, key) => {
      mapping[key] = value;
    });

    saveFileImportConfig({
      feature_type: featureType,
      mapping,
    });
  };

  const handleValidation = () => {
    setLoader(true);
    if (!files.length) {
      toast.error("Please upload file");
      setLoader(false);
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(files[0]);
    reader.addEventListener(
      "load",
      async function (e: ProgressEvent<FileReader>) {
        const input = e.target?.result || null;
        const workbook = new XLJS.Workbook();
        const readAsBuffer = await workbook.xlsx.load(input as Buffer);
        const worksheet = readAsBuffer.worksheets[0];
        try {
          // check required field map
          const mappedField = Array.from(columnMap.values());
          for (const requiredField of destinationFieldRequired) {
            if (!mappedField.includes(requiredField)) {
              throw new Error(`${requiredField} is required to be mapped`);
            }
          }

          for (const [key, value] of columnMap) {
            if (!destinationField.includes(value || "")) {
              columnMap.delete(key);
            }
          }

          // Omit unrecognized destination field
          const parsedData = await parser(
            worksheet,
            [],
            columnMap ? Object.fromEntries(columnMap.entries()) : undefined,
          );

          setRows(parsedData.data);
          setValidatedRow(parsedData.data);
          if (isUseV2 && featureType !== "CUSTOMER") {
            const filteredRow = parsedData.data.filter((d: any) => {
              const fromPreviousUpload = previousSuccessfulImport?.get(
                `${d.company_id}_${d.price_group}_${d.product_ref}`,
              );

              if (!fromPreviousUpload) {
                return true;
              }

              return !isEqual(
                omit(fromPreviousUpload, ["id", "line_no"]),
                omit(d, ["id", "line_no"]),
              );
            });
            setFilteredRow(filteredRow);
          } else {
            setFilteredRow(parsedData.data);
          }
          setError(parsedData.hasError);
          handleNext();
        } catch (e: any) {
          toast.error(e.message);
        }
      },
    );

    setLoader(false);
  };

  const handleOpenColumnMappingStep = () => {
    if (!files.length) {
      toast.error("Please upload file");
      setLoader(false);
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(files[0]);
    reader.addEventListener(
      "load",
      async function (e: ProgressEvent<FileReader>) {
        const input = e.target?.result || null;
        const workbook = new XLJS.Workbook();
        const readAsBuffer = await workbook.xlsx.load(input as Buffer);
        const worksheet = readAsBuffer.worksheets[0];
        try {
          const parsedData = await readRawData(worksheet);
          setRawHeader(parsedData.header);
          setRawRow(parsedData.rows);
          handleNext();
        } catch (e: any) {
          toast.error(e.message);
        }
      },
    );
  };
  const handleConfirmV1Error = (
    result: {
      body: {
        detail: {
          processed: number;
          error_line_number?: number;
          last_error: string;
        };
      };
      error: string;
    },
    successCount: number,
  ) => {
    const { body, error } = result;
    if (error) {
      const lastFailedRowInBatch = body.detail.processed;
      const totalSuccessRow = lastFailedRowInBatch + successCount - 1;

      const rowInfo =
        totalSuccessRow + 1 === validatedRow.length
          ? `line ${validatedRow.length}`
          : `line ${totalSuccessRow + 1} to line ${validatedRow.length} `;
      setErrorText({
        info: `failed to process data in ${
          body.detail.error_line_number
            ? `line ${body.detail.error_line_number}`
            : rowInfo
        }`,
        last_error: `last error: ${body.detail.last_error}`,
      });
      return;
    }
  };

  const handleConfirmV2Result = async (
    result: OrderGuideImportResponse | CustomerImportResponse,
  ) => {
    if (featureType === "CUSTOMER") {
      const { customer_process } = result as CustomerImportResponse;
      setConfirmV2Result((prevResult: ConfirmV2ResultType) => ({
        customer: [
          ...(prevResult.customer || []),
          ...customer_process.success,
          ...customer_process.failed,
        ],
      }));
      return;
    }

    // Handle product/order guide case
    const { product_process, assign_product_process } =
      result as OrderGuideImportResponse;

    setConfirmV2Result((prevResult: ConfirmV2ResultType) => ({
      product: [
        ...(prevResult.product || []),
        ...product_process.success,
        ...product_process.failed,
      ],
      order_guide: [
        ...(prevResult.order_guide || []),
        ...assign_product_process.success,
        ...assign_product_process.failed,
      ],
    }));
  };

  const saveSuccessfulImportToCache = async (
    validatedRow: any[],
    featureType: string,
    uploadFile: Function | undefined,
    saveFileImportConfig: Function,
  ) => {
    const toastLoading = toast.loading(
      "saving successful upload file to the cache",
    );

    try {
      const convertedValidatedRow = validatedRow.reduce((acc, item) => {
        const key = `${item.company_id}_${item.price_group}_${item.product_ref}`;
        acc.set(key, item);
        return acc;
      }, new Map());

      const jsonBlob = new Blob(
        [JSON.stringify(Object.fromEntries(convertedValidatedRow))],
        { type: "application/json" },
      );

      const formData = new FormData();
      formData.append("file", jsonBlob, "import-file-cache.json");
      formData.append("file_type", "IMPORT_CACHE");

      const data = await uploadFile?.({ formData });
      const filename = data.file_name.split("/");

      await saveFileImportConfig({
        feature_type: featureType,
        last_successful_import_cache: filename[filename.length - 1],
      });
    } finally {
      toast.dismiss(toastLoading);
    }
  };

  const handleConfirm = async () => {
    setLoader(true);
    emptyErrorHandler();

    try {
      // Prepare data chunks based on selection
      const rowsToProcess = isUseV2 && enableCache ? filteredRow : validatedRow;
      const chunks = createChunk(rowsToProcess, BATCH_SIZE);

      // Initialize progress tracking
      let successCount = 0;
      if (chunks.length > 1) {
        setProcessed(0);
      }

      // Process all chunks
      for (const batch of chunks) {
        const result = await onConfirm(batch);

        if (isUseV2) {
          await handleConfirmV2Result(result);
        } else {
          handleConfirmV1Error(result, successCount);
        }

        successCount += batch.length;
        setProcessed(successCount);
      }

      toast.success(`successfully insert ${rowsToProcess.length} data`);

      if (!isUseV2) {
        refetchApi?.();
        toggleModal();
        return;
      }

      // Handle V2 post-processing
      if (featureType === "CUSTOMER") {
        const hasErrors =
          (confirmV2Result.customer?.filter((i) => !!i.error).length ?? 1) <= 0;
        if (!hasErrors) {
          refetchApi?.();
          toggleModal();
        }
      } else {
        const hasProductErrors =
          (confirmV2Result.product?.filter((i) => !!i.error).length ?? 1) <= 0;
        const hasOrderGuideErrors =
          (confirmV2Result.order_guide?.filter((i) => !!i.error).length ?? 1) <=
          0;

        if (!hasProductErrors && !hasOrderGuideErrors) {
          await saveSuccessfulImportToCache(
            validatedRow,
            featureType,
            uploadFile,
            saveFileImportConfig,
          );
        }
      }

      handleNext();
    } catch (e: any) {
      setErrorText({
        info: `failed to import data`,
        last_error: "",
      });
    } finally {
      setProcessed(null);
      setLoader(false);
    }
  };

  const steps = useMemo(() => {
    const s = [
      {
        label: "Instructions",
        description: "Follow the instructions",
        component: (
          <InstructionStep
            title={title || "Data"}
            url={templateUrl || "#"}
            videoUrl={videoUrl}
          />
        ),
      },
      {
        label: "Upload",
        description: "Upload your XLSX file",
        component: (
          <FileDropzone
            singleFile
            accept={{ "text/xlsx": [".xlsx"] }}
            caption="(.xlsx files only)"
            files={files}
            onDrop={handleFilesDrop}
            onUpload={handleOpenColumnMappingStep}
            imageUploader={loader}
            onRemove={handleFileRemove}
            onRemoveAll={handleFilesRemoveAll}
          />
        ),
      },
    ];

    s.push({
      label: "Column Mapping",
      description: "Check your XLSX mapping",
      component: (
        <ColumnMapping
          setColumnMap={setColumnMap}
          columnMap={columnMap}
          header={rawHeader}
          rows={rawRow}
          destinationField={destinationField}
          destinationFieldRequired={destinationFieldRequired}
        />
      ),
    });

    s.push({
      label: "Validate",
      description: "Review your data",
      component: (
        <Preview
          column={column}
          rows={rows}
          error={error}
          isUseV2={isUseV2}
          enableCache={enableCache}
          filteredCount={filteredRow.length}
          totalCount={validatedRow.length}
          onEnableCacheChange={setEnableCache}
        />
      ),
    });

    s.push({
      label: "Import",
      description: `Confirm to import ${title}`,
      component: (
        <ConfirmationStep
          title={title || "Products"}
          count={
            isUseV2 && enableCache ? filteredRow.length : validatedRow.length
          }
          isUseV2={!!isUseV2}
          processed={processed}
          enableCache={enableCache}
          onEnableCacheChange={setEnableCache}
          featureType={featureType}
        />
      ),
    });

    if (isUseV2) {
      s.push({
        label: "Result",
        description: "Import finished, below are the import summary",
        component: (
          <Result
            totalData={
              isUseV2 && enableCache ? filteredRow.length : validatedRow.length
            }
            result={confirmV2Result}
            featureType={featureType}
          />
        ),
      });
    }
    return s;
  }, [
    column,
    files,
    handleValidation,
    handleFileRemove,
    handleFilesDrop,
    handleFilesRemoveAll,
    rows,
    enableCache,
    validatedRow.length,
    filteredRow.length,
  ]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        position: "absolute" as const,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      <Paper
        elevation={12}
        sx={{
          padding: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Typography variant="h5">Upload {title} XLSX</Typography>
        </Stack>

        <Box sx={{ width: "100%" }}>
          <Stepper activeStep={activeStep}>
            {steps.map((step, index) => {
              const stepProps: StepProps = {
                sx: {
                  "& .MuiStepLabel-root .Mui-completed": {
                    color: "#5cb85c",
                  },
                },
              };

              const labelProps: StepLabelProps = {};

              if ((error || errorText) && index == activeStep) {
                labelProps.optional = (
                  <Typography variant="caption" color="error">
                    {errorText?.info} <br />
                    {errorText?.last_error}
                  </Typography>
                );
                labelProps.error = true;
              }

              if (isStepSkipped(index)) {
                stepProps.completed = false;
              }

              return (
                <Step key={step.label} {...stepProps}>
                  <StepLabel {...labelProps}>{step.label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        <Stack sx={{ marginTop: "20px" }}>{steps[activeStep].component}</Stack>
        <Stack direction="row" spacing={2}>
          {activeStep > 0 && (
            <Button
              color="inherit"
              onClick={handleBack}
              sx={{
                mr: 1,
              }}
            >
              Back
            </Button>
          )}
          {steps[activeStep].label === "Upload" ? null : steps[activeStep]
              .label === "Column Mapping" ? (
            <Button
              fullWidth
              variant="contained"
              disabled={error}
              onClick={() => {
                handleSaveColumnMapping();
                handleValidation();
              }}
            >
              Validate
            </Button>
          ) : (isUseV2 && activeStep < LIMIT - 1) ||
            (!isUseV2 && activeStep < LIMIT) ? (
            <Button
              fullWidth
              variant="contained"
              disabled={error}
              onClick={handleNext}
            >
              Proceed
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              disabled={isUseV2 && enableCache && filteredRow.length <= 0}
              onClick={
                isUseV2 && activeStep == LIMIT ? toggleModal : handleConfirm
              }
            >
              {loader ? (
                <CircularProgress color="inherit" size={20} />
              ) : isUseV2 && activeStep == LIMIT ? (
                "Close"
              ) : (
                "Import"
              )}
            </Button>
          )}
          <Button
            fullWidth
            onClick={() => {
              handleReset();
              handleFilesRemoveAll();
              toggleModal();
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};
